import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { SlashLink as Link } from './SlashLink';
import { auth } from '../firebase';
import {
  ADMIN_VIEW_MEMBER,
  ADMIN_VIEW_PROSPECT,
  setAdminViewAs,
} from '../utils/adminView';
import useSiteSettings from '../hooks/useSiteSettings';
import useAccess from '../hooks/useAccess';
import { lifetimeCtaPriceUsd } from '../constants/pricing';
import { L1_MONTHLY_SKU, L2_ANNUAL_SKU, L3_ANNUAL_SKU, quoteSku } from '../constants/skus';
import { shouldLeadWithMonthly } from '../utils/priceFraming';
import {
  ADMIN_CONSOLE_LINKS,
  isAdminPath,
  USER_ACCOUNT_LINKS,
  USER_BILLING_LINK,
} from '../constants/navMenus';
import { dashboardPathFromAccess } from '../utils/memberHome';
import { normalizeRoutePath, shouldHideSiteChrome } from '../utils/quizRoutes';
import { mockAuthHref } from '../utils/authMemory';
import { authHref, returnPathFor } from '../utils/safeNext';
import { useQuiz } from './QuizContext';
import NavbarDropdown from './NavbarDropdown';
import NavReferral, { GiftIcon } from './NavReferral';
import useReferralSummary from '../hooks/useReferralSummary';
import { phiTrack } from '../utils/phiTrack';
import { recordReferralNavOpen } from '../utils/referralTracking';
import {
  getPreferredLevel,
  inferLevelFromPath,
  LEVEL_SWITCHER_OPTIONS,
  trackLevelIntent,
} from '../utils/levelPreference';
import '../styles/navbar.css';
import usePricingTier from '../hooks/usePricingTier';

const SideMenu = lazy(() => import('./SideMenu'));
const AntdProvider = lazy(() => import('./AntdProvider'));

const initialsFromUser = (user) => {
  const source = (user?.displayName || user?.email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M3 5.5h14a1 1 0 0 1 0 2H3a1 1 0 1 1 0-2zm0 3.75h14a1 1 0 0 1 0 2H3a1 1 0 1 1 0-2zm0 3.75h14a1 1 0 0 1 0 2H3a1 1 0 1 1 0-2z"
      />
    </svg>
  );
}

const Navbar = () => {
  const regional = usePricingTier(); // re-render CTA copy when the regional sale resolves / ends
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const [activeLevel, setActiveLevel] = useState(() => getPreferredLevel('l1'));
  const { settings } = useSiteSettings();
  const access = useAccess();
  const user = access.user || auth.currentUser;
  const isAdmin = !access.loading && (access.realAdmin || access.isAdmin);
  const { hideSiteChrome, resultsVisible } = useQuiz();
  const showPricing = !!settings.monetizationEnabled;
  const showBilling = showPricing || isAdmin;
  const dashboardPath = user ? dashboardPathFromAccess(access) : '/login?next=/dashboard';
  const location = useLocation();
  const [referOpen, setReferOpen] = useState(false);
  /** Gift + "Refer a friend": signed-in users, never on timed quiz routes (exam runner, drills, results). */
  const showRefer = !!user && !location.pathname.startsWith('/quiz/');
  const referral = useReferralSummary(user, { enabled: showRefer });
  const referralsOn = showRefer && !!referral.data?.enabled;
  /** Results keep the navbar for orientation; the page itself owns the single unlock CTA. */
  const onQuizResults = resultsVisible && !hideSiteChrome;
  const hideNav = shouldHideSiteChrome(location.pathname, hideSiteChrome) && !onQuizResults;
  /** Log in / Create account return here afterwards (not to the dashboard). */
  const authNext = returnPathFor(location);
  const isStudy = location.pathname.startsWith('/study-guide');
  const onPricingPage = normalizeRoutePath(location.pathname) === '/pricing';
  const onAdminPage = isAdmin && isAdminPath(location.pathname);
  const asProspect = isAdmin && access.viewAs === ADMIN_VIEW_PROSPECT;
  /** The CTA follows the level being browsed: /level/2 → free Level 2 Mock 1. */
  const ctaLevel =
    inferLevelFromPath(location.pathname) || (location.pathname === '/' ? 'l1' : activeLevel);
  const ctaLevelNum = ctaLevel === 'l3' ? 3 : ctaLevel === 'l2' ? 2 : 1;
  const paidForCtaLevel =
    ctaLevelNum === 1
      ? access.paid
      : access.hasPackage || (ctaLevelNum === 2 ? access.paidL2 : access.paidL3);
  const showTrialChrome =
    showPricing && !access.loading && !paidForCtaLevel && !access.isAdmin;
  const trialExhausted =
    ctaLevelNum === 1 ? access.freeUsed : ctaLevelNum === 2 ? access.freeUsedL2 : access.freeUsedL3;
  const levelQuizBase = ctaLevelNum === 1 ? '/quiz' : `/quiz/l${ctaLevelNum}`;
  const freeMockPath = `${levelQuizBase}/session-1`;
  const freeMockHref = user || ctaLevelNum === 1 ? freeMockPath : mockAuthHref(freeMockPath);
  const priceUsd =
    ctaLevelNum === 1
      ? lifetimeCtaPriceUsd()
      : quoteSku(ctaLevelNum === 2 ? L2_ANNUAL_SKU : L3_ANNUAL_SKU).priceUsd;
  /** Single navbar purchase label for founding lifetime (not /yr, not "founding"). */
  const lifetimeCtaLabel = `$${priceUsd} Lifetime`;
  const monthlyCtaUsd = quoteSku(L1_MONTHLY_SKU, regional.discounted ? { tier: regional.tier } : {}).priceUsd;
  const pricingNavMonthlyFirst = shouldLeadWithMonthly({
    leadWithMonthly: true,
    showMonthlyOption: true,
    lifetimeFirst: regional.lifetimeFirst,
  });
  const purchaseHref = ctaLevelNum === 1 ? '/pricing#purchase' : `/level/${ctaLevelNum}#purchase`;
  const mockHref = ctaLevelNum === 1 ? '/quiz/session' : levelQuizBase;
  const levelWord = ctaLevelNum === 1 ? '' : `Level ${ctaLevelNum} `;
  const navCtaTo = onPricingPage
    ? '#purchase'
    : showTrialChrome && trialExhausted
      ? purchaseHref
      : showTrialChrome
        ? freeMockHref
        : mockHref;
  const navCtaLabel = onPricingPage
    ? pricingNavMonthlyFirst
      ? `Purchase — from $${monthlyCtaUsd}/mo`
      : `Purchase — $${lifetimeCtaPriceUsd()} Lifetime`
    : showTrialChrome && trialExhausted
      ? ctaLevelNum === 1 && pricingNavMonthlyFirst
        ? `Purchase — from $${monthlyCtaUsd}/mo`
        : lifetimeCtaLabel
      : showTrialChrome
        ? `Start free ${levelWord}Mock 1`
        : ctaLevelNum === 1
          ? 'Timed mocks'
          : `Level ${ctaLevelNum} mocks`;
  const navCtaClass = onPricingPage ? 'nav-cta nav-cta--ghost' : 'nav-cta';
  const compactCtaLabel = onPricingPage
    ? 'Purchase'
    : showTrialChrome && !trialExhausted
      ? `Free ${ctaLevelNum === 1 ? '' : `L${ctaLevelNum} `}Mock 1`
      : navCtaLabel;
  const secondaryLinks = [
    { key: 'topics', label: 'Topics', to: access.paid || access.isAdmin ? '/study-guide' : '/#topics' },
    { key: 'study', label: 'Study guide', to: '/study-guide' },
    { key: 'glossary', label: 'Glossary', to: '/glossary' },
    { key: 'blog', label: 'Blog', to: '/blog' },
    ...(isAdmin ? [{ key: 'admin', label: 'Admin', to: '/admin' }] : []),
  ];
  const levelItems = LEVEL_SWITCHER_OPTIONS.map((level) => ({
    key: level.id,
    label: (
      <Link
        to={level.href}
        aria-current={activeLevel === level.id ? 'page' : undefined}
        onClick={() => trackLevelIntent(level.id, { source: 'level_switcher_link', path: level.href })}
      >
        {level.label}
      </Link>
    ),
  }));

  const accountLinks = useMemo(() => {
    const links = [...USER_ACCOUNT_LINKS];
    if (showBilling) links.push(USER_BILLING_LINK);
    return links;
  }, [showBilling]);

  useEffect(() => {
    const sync = () => setActiveLevel(getPreferredLevel('l1'));
    window.addEventListener('cfa-preferred-level', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('cfa-preferred-level', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const panel = drawerRef.current;
    const trigger = menuButtonRef.current;
    panel?.querySelector('button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'Tab') {
        const targets = [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
          .filter((target) => {
            for (let node = target; node && node !== panel; node = node.parentElement) {
              const style = window.getComputedStyle(node);
              if (style.display === 'none' || style.visibility === 'hidden') return false;
            }
            return true;
          });
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      trigger?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onDoc = (e) => {
      if (!accountRef.current?.contains(e.target)) setAccountOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAccountOpen(false);
        accountRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  useEffect(() => {
    setOpen(false);
    setAccountOpen(false);
    setReferOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 900px)');
    const closeDrawer = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener('change', closeDrawer);
    return () => desktop.removeEventListener('change', closeDrawer);
  }, []);

  if (hideNav) {
    return null;
  }

  return (
    <header
      className={`navbar${onAdminPage ? ' is-admin-context' : ''}${asProspect ? ' is-prospect-preview' : ''}`}
    >
      <div className="navbar-left">
        <Link to={access.user && (access.paid || access.isAdmin) ? '/dashboard' : '/'} className="logo">
          CFA Mock Exam
        </Link>
        <nav className="navbar-links" aria-label="Primary">
          <Link to="/quiz/session">Mocks</Link>
          <NavbarDropdown label="Levels" items={levelItems} selectedKeys={[activeLevel]} />
          {user && <Link to={dashboardPath}>Dashboard</Link>}
          {showPricing && <Link to="/pricing">Pricing</Link>}
          {secondaryLinks.map((link) => (
            <Link key={link.key} to={link.to}
              className={`navbar-secondary-link${link.key === 'admin' ? ' navbar-admin-link' : ''}`}
              aria-current={link.key === 'admin' && onAdminPage ? 'page' : undefined}>
              {link.label}
            </Link>
          ))}
          <NavbarDropdown label="More" className="navbar-more" items={secondaryLinks.map((link) => ({
            key: link.key,
            label: <Link to={link.to} className={link.key === 'admin' ? 'navbar-admin-link' : undefined}
              aria-current={link.key === 'admin' && onAdminPage ? 'page' : undefined}>{link.label}</Link>,
          }))} />
        </nav>
      </div>
      <div className="navbar-right">
        {isAdmin ? (
          <div className="nav-view-toggle" role="group" aria-label="Admin site preview">
            <button
              type="button"
              className={`nav-view-btn${access.viewAs !== ADMIN_VIEW_PROSPECT ? ' is-active' : ''}`}
              onClick={() => setAdminViewAs(ADMIN_VIEW_MEMBER)}
              title="See the site as a paid member / admin"
            >
              Member
            </button>
            <button
              type="button"
              className={`nav-view-btn${access.viewAs === ADMIN_VIEW_PROSPECT ? ' is-active' : ''}`}
              onClick={() => setAdminViewAs(ADMIN_VIEW_PROSPECT)}
              title="Preview founding CTAs and paywalls as a prospect"
            >
              Prospect
            </button>
          </div>
        ) : null}
        {referralsOn ? <NavReferral user={user} summary={referral} open={referOpen} onOpenChange={setReferOpen} /> : null}
        {user ? (
          <div className="nav-account" ref={accountRef}>
            <button
              type="button"
              className="nav-user-btn"
              aria-label="Account menu"
              aria-expanded={accountOpen}
              aria-haspopup="menu"
              onClick={() => setAccountOpen((v) => !v)}
            >
              {user.photoURL ? (
                <img className="nav-user nav-user--img" src={user.photoURL} alt="" />
              ) : (
                <span className="nav-user nav-user--initials">{initialsFromUser(user)}</span>
              )}
            </button>
            {accountOpen ? (
              <div className="nav-account-menu" role="menu">
                <p className="nav-account-label">Your account</p>
                {accountLinks.map((link) => (
                  <Link
                    key={link.key}
                    role="menuitem"
                    to={link.to}
                    onClick={() => setAccountOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                {isAdmin ? (
                  <>
                    <p className="nav-account-label">Preview as</p>
                    <div className="nav-view-toggle nav-view-toggle--account" role="group" aria-label="Admin site preview">
                      <button type="button" className={`nav-view-btn${!asProspect ? ' is-active' : ''}`}
                        onClick={() => setAdminViewAs(ADMIN_VIEW_MEMBER)}>Member</button>
                      <button type="button" className={`nav-view-btn${asProspect ? ' is-active' : ''}`}
                        onClick={() => setAdminViewAs(ADMIN_VIEW_PROSPECT)}>Prospect</button>
                    </div>
                    <p className="nav-account-label">Admin console</p>
                    {ADMIN_CONSOLE_LINKS.map((link) => (
                      <Link
                        key={link.key}
                        role="menuitem"
                        to={link.to}
                        onClick={() => setAccountOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </>
                ) : null}
                <button
                  type="button"
                  className="nav-logout-btn"
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false);
                    auth.signOut();
                  }}
                >
                  Log out
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <Link to={authHref('login', { next: authNext })} className="nav-login">
            Log in
          </Link>
        )}
        {!onQuizResults ? (
          <Link to={navCtaTo} className={navCtaClass} aria-label={navCtaLabel}>
            <span className="nav-cta-full">{navCtaLabel}</span>
            <span className="nav-cta-compact" aria-hidden="true">{compactCtaLabel}</span>
          </Link>
        ) : null}
        <button
          ref={menuButtonRef}
          type="button"
          className="nav-menu-btn"
          aria-label="Open menu"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={open ? 'navbar-drawer' : undefined}
          onClick={() => setOpen(true)}
        >
          <MenuIcon />
        </button>
      </div>

      {open ? createPortal(
        <div className="nav-drawer-root">
          <button
            type="button"
            className="nav-drawer-backdrop"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <aside ref={drawerRef} id="navbar-drawer" className="nav-drawer-panel" role="dialog" aria-modal="true" aria-label="Site menu">
            <div className="nav-drawer-head">
              <span>Menu</span>
              <button type="button" className="nav-drawer-close" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
            <nav className="mobile-drawer-links" aria-label="Mobile navigation" onClick={(event) => {
              if (event.target.closest('a')) setOpen(false);
            }}>
              <Link to="/quiz/session">Mocks</Link>
              {LEVEL_SWITCHER_OPTIONS.map((level) => (
                <Link key={level.id} to={level.href}
                  aria-current={activeLevel === level.id ? 'page' : undefined}
                  onClick={() => trackLevelIntent(level.id, { source: 'level_switcher_link', path: level.href })}>
                  {level.label}
                </Link>
              ))}
              {!onQuizResults && <Link to={navCtaTo} className="mobile-drawer-trial">{navCtaLabel}</Link>}
              <Link to={access.paid || access.isAdmin ? '/study-guide' : '/#topics'}>Topics</Link>
              <Link to={dashboardPath}>Dashboard</Link>
              {showPricing && <Link to="/pricing">Pricing</Link>}
              <Link to="/study-guide">Study guide</Link>
              <Link to="/glossary">Glossary</Link>
              <Link to="/blog">Blog</Link>
              {isAdmin && <Link to="/admin" className="navbar-admin-link">Admin</Link>}
              {user ? (
                <>
                  {referralsOn ? (
                    <button type="button" className="mobile-drawer-refer" onClick={() => {
                      phiTrack('referral_nav_open', { source: 'menu', placement: 'menu' });
                      recordReferralNavOpen('menu');
                      setOpen(false);
                      setReferOpen(true);
                    }}>
                      <GiftIcon /> Refer a friend
                    </button>
                  ) : null}
                  <p className="mobile-drawer-label">Your account</p>
                  {USER_ACCOUNT_LINKS.map((link) => (
                    <Link key={link.key} to={link.to}>
                      {link.label}
                    </Link>
                  ))}
                  {showBilling && (
                    <Link to={USER_BILLING_LINK.to}>{USER_BILLING_LINK.label}</Link>
                  )}
                  {isAdmin && (
                    <>
                      <p className="mobile-drawer-label mobile-drawer-label-admin">Preview as</p>
                      <div
                        className="nav-view-toggle nav-view-toggle--drawer"
                        role="group"
                        aria-label="Admin site preview"
                      >
                        <button
                          type="button"
                          className={`nav-view-btn${access.viewAs !== ADMIN_VIEW_PROSPECT ? ' is-active' : ''}`}
                          onClick={() => setAdminViewAs(ADMIN_VIEW_MEMBER)}
                        >
                          Member
                        </button>
                        <button
                          type="button"
                          className={`nav-view-btn${access.viewAs === ADMIN_VIEW_PROSPECT ? ' is-active' : ''}`}
                          onClick={() => setAdminViewAs(ADMIN_VIEW_PROSPECT)}
                        >
                          Prospect
                        </button>
                      </div>
                      <p className="mobile-drawer-label mobile-drawer-label-admin">Admin console</p>
                      {ADMIN_CONSOLE_LINKS.map((link) => (
                        <Link key={link.key} to={link.to} className="mobile-drawer-admin-link">
                          {link.label}
                        </Link>
                      ))}
                    </>
                  )}
                  <button type="button" className="mobile-drawer-logout" onClick={() => auth.signOut()}>
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to={authHref('login', { next: authNext })}>Log in</Link>
                  <Link to={authHref('signup', { next: authNext })}>Create account</Link>
                </>
              )}
            </nav>
            {isStudy && (
              <div style={{ marginTop: 24 }}>
                <Suspense fallback={null}>
                  <AntdProvider>
                    <SideMenu />
                  </AntdProvider>
                </Suspense>
              </div>
            )}
          </aside>
        </div>, document.body
      ) : null}
    </header>
  );
};

export default Navbar;
